// Pure SVG-string builders for the dashboard charts — a faithful 1:1 port of the Jmix
// `DashboardSvg.java` (line chart, semicircle capacity gauge, occupancy donut). Strokes/fills are
// carried by CSS classes (`.fh-*`, themed in Dashboard.css); the soft area/gauge gradients are inlined
// because gradient stops cannot be expressed as CSS classes. The returned strings are injected via
// `dangerouslySetInnerHTML`, exactly as the Jmix view set them via `Div.innerHTML`.

function fmt(v: number): string {
  return v.toFixed(2);
}

function escape(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function xAt(i: number, n: number, left: number, right: number): number {
  if (n <= 1) return Math.round((left + right) / 2);
  return left + Math.round(((right - left) * i) / (n - 1));
}

function yAt(value: number, max: number, top: number, bottom: number): number {
  return bottom - Math.round(((bottom - top) * value) / max);
}

function polyline(
  cls: string,
  values: number[],
  max: number,
  n: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): string {
  const pts = values
    .map((v, i) => `${xAt(i, n, left, right)},${yAt(v, max, top, bottom)}`)
    .join(" ");
  return `<polyline class='${cls}' fill='none' points='${pts}'/>`;
}

function area(
  fill: string,
  values: number[],
  max: number,
  n: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): string {
  let d = "";
  values.forEach((v, i) => {
    d += `${i === 0 ? "M " : " L "}${xAt(i, n, left, right)} ${yAt(v, max, top, bottom)}`;
  });
  d += ` L ${right} ${bottom} L ${left} ${bottom} Z`;
  return `<path class='fh-area' fill='${fill}' stroke='none' d='${d}'/>`;
}

function dots(
  cls: string,
  values: number[],
  max: number,
  n: number,
  left: number,
  right: number,
  top: number,
  bottom: number,
): string {
  return values
    .map(
      (v, i) =>
        `<circle class='${cls}' cx='${xAt(i, n, left, right)}' cy='${yAt(v, max, top, bottom)}' r='5'/>`,
    )
    .join("");
}

/** Rich area line chart — intake vs. outflow. `xLabels` are pre-formatted; `maxValue` scales the y-axis. */
export function lineChart(
  xLabels: string[],
  intake: number[],
  outflow: number[],
  maxValue: number,
): string {
  const w = 720;
  const h = 285;
  const plotLeft = 58;
  const plotRight = 702;
  const plotTop = 18;
  const plotBottom = 242;
  const max = Math.max(1, maxValue);
  const n = intake.length;

  let svg =
    `<svg class='fh-line-chart' viewBox='0 0 ${w} ${h}' preserveAspectRatio='none' ` +
    `xmlns='http://www.w3.org/2000/svg' role='img' aria-label='Instroom versus uitstroom'>`;
  svg +=
    "<defs>" +
    "<linearGradient id='fhGreenArea' x1='0' x2='0' y1='0' y2='1'>" +
    "<stop offset='0%' stop-color='#2f7d4a' stop-opacity='0.18'/>" +
    "<stop offset='100%' stop-color='#2f7d4a' stop-opacity='0'/></linearGradient>" +
    "<linearGradient id='fhBlueArea' x1='0' x2='0' y1='0' y2='1'>" +
    "<stop offset='0%' stop-color='#226ac9' stop-opacity='0.16'/>" +
    "<stop offset='100%' stop-color='#226ac9' stop-opacity='0'/></linearGradient>" +
    "</defs>";

  for (let g = 0; g <= 4; g++) {
    const y = plotBottom - ((plotBottom - plotTop) * g) / 4;
    const val = Math.round((max * g) / 4);
    svg += `<line class='fh-grid' x1='${plotLeft}' y1='${y}' x2='${plotRight}' y2='${y}'/>`;
    svg += `<text class='fh-axis' x='${plotLeft - 12}' y='${y + 4}' text-anchor='end'>${val}</text>`;
  }

  if (n > 1) {
    svg += area("url(#fhGreenArea)", intake, max, n, plotLeft, plotRight, plotTop, plotBottom);
    svg += area("url(#fhBlueArea)", outflow, max, n, plotLeft, plotRight, plotTop, plotBottom);
    svg += polyline("fh-line fh-line--in", intake, max, n, plotLeft, plotRight, plotTop, plotBottom);
    svg += polyline("fh-line fh-line--out", outflow, max, n, plotLeft, plotRight, plotTop, plotBottom);
    svg += dots("fh-dot fh-dot--in", intake, max, n, plotLeft, plotRight, plotTop, plotBottom);
    svg += dots("fh-dot fh-dot--out", outflow, max, n, plotLeft, plotRight, plotTop, plotBottom);
  }

  xLabels.forEach((lbl, i) => {
    const x = xAt(i, n, plotLeft, plotRight);
    svg += `<text class='fh-xlabel' x='${x}' y='${plotBottom + 24}' text-anchor='middle'>${escape(lbl)}</text>`;
  });

  svg += "</svg>";
  return svg;
}

function polar(cx: number, cy: number, r: number, angleDeg: number): [number, number] {
  const a = (angleDeg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
}

function arc(
  cls: string,
  cx: number,
  cy: number,
  r: number,
  stroke: number,
  startAngle: number,
  sweep: number,
): string {
  const endAngle = startAngle + sweep;
  const p1 = polar(cx, cy, r, startAngle);
  const p2 = polar(cx, cy, r, endAngle);
  const largeArc = sweep > 180 ? 1 : 0;
  return (
    `<path class='${cls}' fill='none' stroke-width='${stroke}' stroke-linecap='round' ` +
    `d='M ${fmt(p1[0])} ${fmt(p1[1])} A ${fmt(r)} ${fmt(r)} 0 ${largeArc} 1 ${fmt(p2[0])} ${fmt(p2[1])}'/>`
  );
}

function arcStroke(
  strokeValue: string,
  cx: number,
  cy: number,
  r: number,
  stroke: number,
  startAngle: number,
  sweep: number,
): string {
  const endAngle = startAngle + sweep;
  const p1 = polar(cx, cy, r, startAngle);
  const p2 = polar(cx, cy, r, endAngle);
  const largeArc = sweep > 180 ? 1 : 0;
  return (
    `<path fill='none' stroke='${strokeValue}' stroke-width='${stroke}' stroke-linecap='round' ` +
    `d='M ${fmt(p1[0])} ${fmt(p1[1])} A ${fmt(r)} ${fmt(r)} 0 ${largeArc} 1 ${fmt(p2[0])} ${fmt(p2[1])}'/>`
  );
}

/** Upper-semicircle capacity gauge. Over capacity (>100%) the fill turns amber. */
export function gauge(present: number, capacity: number): string {
  const cx = 120;
  const cy = 116;
  const r = 95;
  const stroke = 12;
  const over = capacity > 0 && present > capacity;
  const frac = capacity > 0 ? Math.min(1.0, present / capacity) : 0;

  let svg =
    "<svg class='fh-gauge' viewBox='0 0 240 128' xmlns='http://www.w3.org/2000/svg' " +
    "role='img' aria-label='Bezetting'>";
  svg +=
    "<defs>" +
    "<linearGradient id='fhGaugeOk' x1='0' y1='0' x2='1' y2='0'>" +
    "<stop offset='0%' stop-color='#86ab95'/><stop offset='100%' stop-color='#4e6f5f'/></linearGradient>" +
    "<linearGradient id='fhGaugeOver' x1='0' y1='0' x2='1' y2='0'>" +
    "<stop offset='0%' stop-color='#e2b663'/><stop offset='100%' stop-color='#bd7d1a'/></linearGradient>" +
    "</defs>";
  svg += arc("fh-gauge-track", cx, cy, r, stroke, 180, 180);
  if (capacity > 0 && frac > 0) {
    const sweep = Math.max(3.0, 180.0 * frac);
    svg += arcStroke(`url(#${over ? "fhGaugeOver" : "fhGaugeOk"})`, cx, cy, r, stroke, 180, sweep);
  }
  svg += "</svg>";
  return svg;
}

/** Up to 6 slices get distinct tone classes; the centre is left for an HTML overlay. */
export function donut(slices: { label: string; value: number }[], total: number): string {
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const r = 78;
  const stroke = 26;
  const safeTotal = Math.max(1, total);

  let svg =
    `<svg class='fh-donut-svg' viewBox='0 0 ${size} ${size}' xmlns='http://www.w3.org/2000/svg' ` +
    "role='img' aria-label='Bezetting per vestiging'>";
  svg += `<circle class='fh-donut-track' cx='${cx}' cy='${cy}' r='${r}' fill='none' stroke-width='${stroke}'/>`;

  let startAngle = -90.0;
  let idx = 0;
  for (const slice of slices) {
    const frac = slice.value / safeTotal;
    const sweep = frac * 360.0;
    if (sweep <= 0) continue;
    svg += arc(`fh-donut-seg fh-donut-seg--${idx % 6}`, cx, cy, r, stroke, startAngle, sweep);
    startAngle += sweep;
    idx++;
  }
  svg += "</svg>";
  return svg;
}
